import { useEffect, useState } from "react";
import { joinOpenTogetherApi, leaveOpenTogetherApi, openTogetherStatusApi } from "../../../giftsApi";
import { onOpenTogetherWaiting, onSocketOpen } from "../../../chatSocket";
import type { Gift } from "../../../types";

// Private Drops V2 Part B - rendered by PrivateDropLockedPage.tsx once a
// gift.openTogether drop is unlocked but the reveal hasn't started yet.
// Landing on this page (an explicit navigation, not a passive background
// event) IS the "I'm ready" signal, so joining happens automatically on
// mount - the actual synchronized reveal itself is still entirely
// server-authoritative (see server.js's /open-together/join) and handled
// globally by GiftsScreen.tsx's onOpenTogetherStart subscription, which
// opens the reveal dialog the instant the server confirms both sides are
// here, regardless of which screen either person is currently on.
export function OpenTogetherWaitingRoom({ gift }: { gift: Gift }) {
  const [partnerJoined, setPartnerJoined] = useState(false);
  const [partnerOnline, setPartnerOnline] = useState(false);
  const partnerName = gift.direction === "sent" ? gift.recipientName : gift.senderName;

  useEffect(() => {
    let cancelled = false;
    joinOpenTogetherApi(gift.id).then((status) => {
      if (cancelled || !status) return;
      setPartnerJoined(status.partnerJoined);
      setPartnerOnline(status.partnerOnline);
    });
    return () => {
      cancelled = true;
      leaveOpenTogetherApi(gift.id);
    };
  }, [gift.id]);

  useEffect(() => {
    return onOpenTogetherWaiting((data) => {
      if (data.giftId === gift.id) setPartnerJoined(data.partnerJoined);
    });
  }, [gift.id]);

  // Resync on reconnect - a socket drop between mounting and the reveal
  // actually starting would otherwise leave this waiting forever if the
  // open_together_start push happened to arrive during the gap.
  useEffect(() => {
    return onSocketOpen(() => {
      openTogetherStatusApi(gift.id).then((status) => {
        if (status) setPartnerJoined(status.partnerJoined);
      });
    });
  }, [gift.id]);

  return (
    <div className="private-drop-waiting-room">
      <span className="private-drop-waiting-icon" aria-hidden="true">
        <i className="bi bi-people-fill" />
      </span>
      <h2 className="private-drop-waiting-title">Open it together</h2>
      <p className="private-drop-waiting-status">{partnerJoined ? `${partnerName} is here…` : `Waiting for ${partnerName}…`}</p>
      {!partnerJoined && (
        <p className="private-drop-waiting-presence">
          {partnerOnline ? `${partnerName} is online right now.` : `${partnerName} isn't online right now.`}
        </p>
      )}
    </div>
  );
}
